import * as XLSX from 'xlsx';

// Função para criar e baixar o template da planilha de equipamentos
export const downloadEquipamentosTemplate = () => {
    // Criar dados do template
    const templateData = [
        {
            'nserie': 'ABC123456789',
            'nmac': 'C074ADC8A247',
            'netiqueta': 'VOX001234'
        },
        {
            'nserie': '',
            'nmac': '',
            'netiqueta': ''
        }
    ];

    // Criar workbook
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(templateData);

    // Definir larguras das colunas
    const colWidths = [
        { wch: 20 }, // nserie
        { wch: 20 }, // nmac
        { wch: 15 }  // netiqueta
    ];
    ws['!cols'] = colWidths;

    // Adicionar worksheet ao workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Equipamentos');

    // Criar segunda aba com instruções
    const instrucoesData = [
        { 
            'Campo': 'nserie', 
            'Descrição': 'Número de série do equipamento', 
            'Obrigatório': 'Sim', 
            'Exemplo': 'ABC123456789',
            'Observações': 'Deve ser único para cada equipamento'
        },
        { 
            'Campo': 'nmac', 
            'Descrição': 'Endereço MAC do equipamento', 
            'Obrigatório': 'Não', 
            'Exemplo': 'C074ADC8A247 ou 00:1B:44:11:3A:B7',
            'Observações': 'Aceita formatos: sem separadores (C074ADC8A247), com dois pontos (:) ou hífens (-). Será convertido automaticamente para formato sem separadores em maiúsculo'
        },
        { 
            'Campo': 'netiqueta', 
            'Descrição': 'Número da etiqueta patrimonial', 
            'Obrigatório': 'Sim', 
            'Exemplo': 'VOX001234',
            'Observações': 'Identificação única do patrimônio'
        }
    ];

    const wsInstrucoes = XLSX.utils.json_to_sheet(instrucoesData);
    const colWidthsInstrucoes = [
        { wch: 15 }, // Campo
        { wch: 40 }, // Descrição
        { wch: 15 }, // Obrigatório
        { wch: 20 }, // Exemplo
        { wch: 35 }  // Observações
    ];
    wsInstrucoes['!cols'] = colWidthsInstrucoes;
    
    XLSX.utils.book_append_sheet(wb, wsInstrucoes, 'Instruções');

    // Gerar arquivo e fazer download
    const today = new Date();
    const dateString = today.getFullYear() + 
        ('0' + (today.getMonth() + 1)).slice(-2) + 
        ('0' + today.getDate()).slice(-2);
    
    XLSX.writeFile(wb, `template_equipamentos_${dateString}.xlsx`);
};

// Função para processar arquivo XLSX de equipamentos
export const processEquipamentosXlsxFile = async (file, marcas = [], modelos = []) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        
        reader.onload = (event) => {
            try {
                const data = event.target.result;
                const workbook = XLSX.read(data, { type: 'binary' });
                
                // Pegar a primeira planilha
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                
                // Converter para JSON
                const jsonData = XLSX.utils.sheet_to_json(worksheet);
                
                if (jsonData.length === 0) {
                    reject(new Error('A planilha está vazia ou não contém dados válidos'));
                    return;
                }

                // Processar os dados
                const equipamentos = [];
                const erros = [];
                const numerosSerieVistos = new Set();
                const etiquetasVistas = new Set();
                
                jsonData.forEach((row, index) => {
                    const rowNumber = index + 2; // +2 porque o índice começa em 0 e a primeira linha é o cabeçalho
                    
                    // Verificar campos obrigatórios
                    const nserie = row['nserie']?.toString().trim();
                    const nmac = row['nmac']?.toString().trim();
                    const netiqueta = row['netiqueta']?.toString().trim();

                    // Validar campos obrigatórios
                    if (!nserie) {
                        erros.push(`Linha ${rowNumber}: Número de série (nserie) é obrigatório`);
                        return;
                    }
                    if (!netiqueta) {
                        erros.push(`Linha ${rowNumber}: Número da etiqueta (netiqueta) é obrigatório`);
                        return;
                    }

                    // Verificar duplicatas
                    if (numerosSerieVistos.has(nserie)) {
                        erros.push(`Linha ${rowNumber}: Número de série "${nserie}" já existe na planilha`);
                        return;
                    }
                    if (etiquetasVistas.has(netiqueta)) {
                        erros.push(`Linha ${rowNumber}: Etiqueta "${netiqueta}" já existe na planilha`);
                        return;
                    }

                    // Adicionar aos conjuntos de controle
                    numerosSerieVistos.add(nserie);
                    etiquetasVistas.add(netiqueta);

                    // Validar e formatar MAC (se fornecido)
                    let macFormatado = '';
                    if (nmac && nmac !== '') {
                        // Remover todos os caracteres que não são hexadecimais
                        const macLimpo = nmac.replace(/[^0-9A-Fa-f]/g, '');
                        
                        // Validar se tem exatamente 12 caracteres hexadecimais
                        if (macLimpo.length === 12) {
                            // Manter formato sem separadores, apenas maiúsculo
                            macFormatado = macLimpo.toUpperCase();
                        } else if (macLimpo.length > 0) {
                            erros.push(`Linha ${rowNumber}: MAC "${nmac}" deve ter exatamente 12 caracteres hexadecimais (atual: ${macLimpo.length})`);
                            return;
                        }
                    }

                    // Buscar marca e modelo baseado no número de série ou etiqueta
                    let marcaEncontrada = null;
                    let modeloEncontrado = null;
                    
                    if (marcas && marcas.length > 0) {
                        // Buscar por número de série
                        marcaEncontrada = marcas.find((marca) => 
                            nserie.toLowerCase().includes(marca.nome.toLowerCase()) ||
                            netiqueta.toLowerCase().includes(marca.nome.toLowerCase())
                        );
                    }
                    
                    if (modelos && modelos.length > 0) {
                        // Buscar por número de série ou etiqueta
                        modeloEncontrado = modelos.find((modelo) => 
                            nserie.toLowerCase().includes(modelo.nome.toLowerCase()) ||
                            netiqueta.toLowerCase().includes(modelo.nome.toLowerCase())
                        );
                    }
                    
                    // Se encontrou modelo mas não marca, buscar marca do modelo
                    if (!marcaEncontrada && modeloEncontrado && marcas) {
                        marcaEncontrada = marcas.find((marca) => marca.id === modeloEncontrado.id_marca);
                    }

                    // Criar objeto do equipamento
                    equipamentos.push({
                        id: index + 1,
                        nserie: nserie,
                        nmac: macFormatado || '', // Usar o MAC formatado
                        netiqueta: netiqueta,
                        cliente: null, // Será selecionado pelo usuário
                        anexo: null, // Será selecionado pelo usuário
                        marca: marcaEncontrada?.id || null,
                        modelo: modeloEncontrado?.id || null
                    });
                });

                if (erros.length > 0) {
                    reject(new Error(`Erros encontrados na planilha:\n${erros.join('\n')}`));
                    return;
                }

                if (equipamentos.length === 0) {
                    reject(new Error('Nenhum equipamento válido encontrado na planilha'));
                    return;
                }

                // Retornar dados processados
                const result = {
                    equipamentos: equipamentos,
                    tipoImportacao: 'equipamentos_xlsx',
                    totalEquipamentos: equipamentos.length
                };

                resolve(result);
                
            } catch (error) {
                reject(new Error(`Erro ao processar planilha: ${error.message}`));
            }
        };
        
        reader.onerror = () => {
            reject(new Error('Erro ao ler o arquivo Excel'));
        };
        
        reader.readAsBinaryString(file);
    });
};
